import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';

const DOC_VERSION = 'v5.1.0-desktop';
const MASTHEAD_H = 48;

const NAV_ITEMS = [
  { group: 'Início', label: 'Visão geral', tab: 0, id: 'sec-overview' },
  { group: 'Início', label: 'Fundamentos', tab: 0, id: 'sec-foundations' },
  { group: 'Início', label: 'Governança', tab: 0, id: 'sec-governance' },
  { group: 'Estilos', label: 'Cores', tab: 1, id: 'sec-colors' },
  { group: 'Estilos', label: 'Tipografia', tab: 1, id: 'sec-typography' },
  { group: 'Estilos', label: 'Espaçamento', tab: 1, id: 'sec-spacing' },
  { group: 'Estilos', label: 'Movimento', tab: 1, id: 'sec-motion' },
  { group: 'Componentes', label: 'Biblioteca', tab: 0, id: 'sec-library' },
  { group: 'Componentes', label: 'Engenharia reversa', tab: 0, id: 'sec-reverse-engineering' },
  { group: 'Componentes', label: 'Padrão alvo: Button/Form', tab: 0, id: 'sec-target-core' },
  { group: 'Componentes', label: 'Especificação técnica', tab: 0, id: 'sec-component-blueprints' },
  { group: 'Componentes', label: 'Mapa Carbon', tab: 0, id: 'sec-carbon-placement' },
  { group: 'Componentes', label: 'Uso', tab: 1, id: 'sec-usage' },
  { group: 'Componentes', label: 'Código', tab: 2, id: 'sec-tokens' },
  { group: 'Componentes', label: 'Snippets', tab: 2, id: 'sec-code-snippets' },
  { group: 'Componentes', label: 'API', tab: 2, id: 'sec-api' },
  { group: 'Qualidade', label: 'Acessibilidade', tab: 1, id: 'sec-a11y' },
  { group: 'Qualidade', label: 'Testes', tab: 2, id: 'sec-testing' },
  { group: 'Qualidade', label: 'Quality gates', tab: 2, id: 'sec-quality' }
];

const NAV_GROUPS = ['Início', 'Estilos', 'Componentes', 'Qualidade'];

const COMPONENT_LIBRARY = [
  ['AppShell', 'UI Shell', 'Pronto', 'src/components/AppShell.js', 'Container estrutural da aplicação desktop'],
  ['Header', 'UI Shell', 'Pronto', 'src/components/Header.js', 'Cabeçalho global com navegação principal'],
  ['Footer', 'UI Shell', 'Pronto', 'src/components/Footer.js', 'Rodapé institucional'],
  ['FloatingMenu', 'Navegação', 'Pronto', 'src/components/FloatingMenu.js', 'Atalhos contextuais e navegação auxiliar'],
  ['SectionWrapper', 'Navegação', 'Pronto', 'src/components/SectionWrapper.js', 'Isolamento estrutural por rota/seção'],
  ['ScrollToHash', 'Navegação', 'Pronto', 'src/components/ScrollToHash.js', 'Âncoras e rolagem para hash'],
  ['Button', 'Ações', 'Pronto', 'MuiButton override', 'radius 14 + padding 12x32'],
  ['EnterpriseCard', 'Conteúdo e dados', 'Pronto', 'src/components/EnterpriseCard.js', 'Card corporativo com variações visuais'],
  ['Input', 'Formulários', 'Pronto', 'MuiOutlinedInput override', 'focus/error border #CC0000'],
  ['Select', 'Formulários', 'Pronto', 'MuiOutlinedInput + Select', 'aria-expanded + teclado'],
  ['RichTextEditor', 'Formulários', 'Pronto', 'src/components/RichTextEditor.js', 'Editor rico para conteúdo com validações'],
  ['SimpleEditor', 'Formulários', 'Pronto', 'src/components/SimpleEditor.js', 'Editor simplificado para fluxo rápido'],
  ['ImageUpload', 'Formulários', 'Pronto', 'src/components/ImageUpload.js', 'Upload unitário com validação'],
  ['MultiImageUpload', 'Formulários', 'Pronto', 'src/components/MultiImageUpload.js', 'Upload múltiplo de imagens'],
  ['SortableItem', 'Formulários', 'Pronto', 'src/components/SortableItem.js', 'Ordenação drag-and-drop de itens'],
  ['Toast', 'Feedback', 'Pronto', 'src/components/Toast.js', 'success/info/warning/error'],
  ['CookieConsent', 'Feedback', 'Pronto', 'src/components/CookieConsent.js', 'Consentimento LGPD com preferências'],
  ['GenesisScrollIndicator', 'Feedback', 'Pronto', 'src/components/GenesisScrollIndicator.js', 'Indicador de continuidade da leitura'],
  ['Modal/Dialog', 'Sobreposição', 'Pronto', 'MuiDialog override', 'paper radius 14'],
  ['Drawer', 'Sobreposição', 'Pronto', 'MuiDrawer override', 'paper radius 14'],
  ['Chip', 'Exibição', 'Pronto', 'MuiChip override', 'fontWeight 700'],
  ['BottomNavigationAction', 'Navegação', 'Pronto', 'MuiBottomNavigationAction', 'minWidth 72 + label 12px'],
  ['Accordion', 'Conteúdo e dados', 'Pronto', 'MuiAccordion override', 'radius 14 + no before line'],
  ['HeroSection', 'Conteúdo e dados', 'Pronto', 'src/components/HeroSection.js', 'Bloco hero da home desktop'],
  ['ImpactSection', 'Conteúdo e dados', 'Pronto', 'src/components/ImpactSection.js', 'Narrativa de impacto e autoridade'],
  ['ImpactExampleSection', 'Conteúdo e dados', 'Pronto', 'src/components/ImpactExampleSection.js', 'Exemplos táticos de execução'],
  ['ServicesSection', 'Conteúdo e dados', 'Pronto', 'src/components/ServicesSection.js', 'Portfólio e serviços'],
  ['WhatWeDoSection', 'Conteúdo e dados', 'Pronto', 'src/components/WhatWeDoSection.js', 'Motores/áreas de atuação'],
  ['NewsGrid', 'Conteúdo e dados', 'Pronto', 'src/components/NewsGrid.js', 'Grade editorial de notícias'],
  ['NewsJournalCard', 'Conteúdo e dados', 'Pronto', 'src/components/news/NewsJournalCard.js', 'Card editorial de notícia'],
  ['NewsReaderLayout', 'Navegação', 'Pronto', 'src/components/news/NewsReaderLayout.js', 'Layout de leitura e navegação contextual'],
  ['NewsHeader', 'Conteúdo e dados', 'Pronto', 'src/components/news/NewsHeader.js', 'Metadados editoriais do artigo'],
  ['NewsContent', 'Conteúdo e dados', 'Pronto', 'src/components/news/NewsContent.js', 'Renderização de conteúdo jornalístico'],
  ['NewsGallery', 'Conteúdo e dados', 'Pronto', 'src/components/news/NewsGallery.js', 'Galeria visual e mídia'],
  ['NewsShareWidget', 'Ações', 'Pronto', 'src/components/news/NewsShareWidget.js', 'Ações de compartilhamento'],
  ['LegalDocument', 'Governança', 'Pronto', 'src/components/legal/LegalDocument.js', 'Documentos legais e compliance'],
  ['AuditSeal', 'Governança', 'Pronto', 'src/components/legal/AuditSeal.js', 'Selo de auditoria/documento oficial'],
  ['OverviewHero', 'Conteúdo e dados', 'Pronto', 'src/components/overview/OverviewHero.js', 'Hero da seção Sobre/Visão geral'],
  ['OverviewCTA', 'Ações', 'Pronto', 'src/components/overview/OverviewCTA.js', 'Call-to-action da seção institucional'],
  ['OverviewEssence', 'Conteúdo e dados', 'Pronto', 'src/components/overview/OverviewEssence.js', 'Bloco de essência institucional'],
  ['OverviewExpertise', 'Conteúdo e dados', 'Pronto', 'src/components/overview/OverviewExpertise.js', 'Bloco de expertise'],
  ['ValueCard', 'Conteúdo e dados', 'Pronto', 'src/components/purpose/ValueCard.js', 'Cartão de valores e proposta'],
  ['TacticalLayout', 'Conteúdo e dados', 'Pronto', 'src/components/tactical/TacticalLayout.js', 'Layout técnico de operação'],
  ['TacticalGrid', 'Conteúdo e dados', 'Pronto', 'src/components/tactical/TacticalGrid.js', 'Grid técnico responsivo'],
  ['DataStream', 'Conteúdo e dados', 'Pronto', 'src/components/tactical/DataStream.js', 'Fluxo visual de dados'],
  ['ReticleCursor', 'Conteúdo e dados', 'Pronto', 'src/components/tactical/ReticleCursor.js', 'Cursor customizado de precisão']
];

const CARBON_PLACEMENT_MATRIX = [
  ['Ações (Actions)', 'Button, OverviewCTA, NewsShareWidget', 'Ações primárias e secundárias'],
  ['Formulários (Forms)', 'Input, Select, RichTextEditor, SimpleEditor, ImageUpload, MultiImageUpload, SortableItem', 'Entrada e edição de dados'],
  ['Navegação (Navigation)', 'Header, Footer, FloatingMenu, SectionWrapper, ScrollToHash, NewsReaderLayout, BottomNavigationAction', 'Orientação, rotas e contexto'],
  ['Feedback (Notifications/Status)', 'Toast, CookieConsent, GenesisScrollIndicator', 'Resposta de sistema e consentimento'],
  ['Sobreposição (Overlay)', 'Modal/Dialog, Drawer', 'Interações contextuais em camada superior'],
  ['Conteúdo e dados (Data display)', 'EnterpriseCard, NewsGrid, NewsJournalCard, NewsHeader, NewsContent, NewsGallery, Accordion', 'Visualização editorial e estrutural'],
  ['UI Shell', 'AppShell + Header/Footer', 'Estrutura macro da aplicação desktop'],
  ['Governança', 'LegalDocument, AuditSeal', 'Conformidade, trilha legal e confiança']
];

const REVERSE_ENGINEERING_MATRIX = [
  ['Button', '57 usos em 17 arquivos', '67 usos em 24 arquivos', 'Padronizar variantes (primary/secondary/ghost) via token de ação'],
  ['Form Field (Input/Select)', 'TextField 32 + Select 2', 'TextField 33 + Select 3', 'Criar contrato único de field (label, helper, error, hint)'],
  ['Modal/Dialog', '4 usos em 3 arquivos', '4 usos em 3 arquivos', 'Consolidar wrapper de diálogo com slots fixos (title/content/actions)'],
  ['Table', '0 uso MUI Table no portal real', '0 uso MUI Table no portal real', 'Definir padrão único para dados tabulares e estado empty/loading'],
  ['Card', '3 usos em 3 arquivos', '2 usos em 2 arquivos', 'Unificar card editorial/institucional com tokens de elevação e spacing']
];

const CORE_UI_BLUEPRINTS = [
  {
    name: 'Button',
    sketch: 'button',
    anatomy: 'container, label, ícone opcional',
    states: 'default, hover, active, focus-visible, disabled, loading',
    a11y: 'foco visível + área mínima 44px',
    desktopRef: 'components/HeroSection.js',
    mobileRef: 'components/HeroSection.mobile.js'
  },
  {
    name: 'Form Field',
    sketch: 'input',
    anatomy: 'label, campo, helper/error, affordance',
    states: 'default, focus, filled, error, disabled',
    a11y: 'label real + aria-invalid + descrição associada',
    desktopRef: 'pages/AdminPanel.js',
    mobileRef: 'pages/AdminPanel.js'
  },
  {
    name: 'Modal/Dialog',
    sketch: 'dialog',
    anatomy: 'backdrop, title, content, actions',
    states: 'closed, opening, open, closing',
    a11y: 'focus trap + esc + aria-modal',
    desktopRef: 'components/CookieConsent.js',
    mobileRef: 'components/CookieConsent.js'
  },
  {
    name: 'Table',
    sketch: 'table',
    anatomy: 'header, linhas, célula de ação',
    states: 'loading, populated, empty',
    a11y: 'headers semânticos + leitura por teclado',
    desktopRef: 'padrão ainda não consolidado',
    mobileRef: 'padrão ainda não consolidado'
  },
  {
    name: 'Card',
    sketch: 'card',
    anatomy: 'container, heading, suporte visual, bloco textual',
    states: 'default, hover, selected/featured',
    a11y: 'hierarquia de heading e contraste de leitura',
    desktopRef: 'components/EnterpriseCard.js',
    mobileRef: 'components/EnterpriseCard.js'
  }
];

const BUTTON_TARGET_MATRIX = [
  ['Altura', '44px (sm), 48px (md), 52px (lg)'],
  ['Raio', '14px (token global de canal)'],
  ['Padding horizontal', '24px (sm), 28px (md), 32px (lg)'],
  ['Peso tipográfico', '600 (button token)'],
  ['Variantes', 'primary, secondary, ghost, destructive'],
  ['Estados', 'default, hover, active, focus-visible, disabled, loading'],
  ['Foco', 'outline 2px + offset para contraste AA']
];

const FORM_FIELD_TARGET_MATRIX = [
  ['Estrutura', 'label + input/select + helper/error + contador opcional'],
  ['Altura', '44px (sm), 48px (md)'],
  ['Borda', '1px subtle / 1px brand no foco / 1px error no erro'],
  ['Raio', '14px (token global de canal)'],
  ['Espaçamento interno', '12px vertical, 14px horizontal'],
  ['Estados', 'default, focus, filled, error, disabled, readonly'],
  ['A11y', 'id/htmlFor, aria-invalid, aria-describedby, teclado completo']
];

const CORE_MIGRATION_STEPS = [
  ['Onda 1', 'Criar wrappers R5Button e R5FormField', 'Sem trocar UI final; apenas contrato'],
  ['Onda 2', 'Migrar Home + Contato + Admin', 'Maior ganho com baixo risco'],
  ['Onda 3', 'Migrar Notícias + Sobre + Propósito', 'Consolidar variações editoriais'],
  ['Gate', 'Build + check:channel-isolation + smoke visual', 'Bloquear regressão cruzada']
];

const CORE_PRIMITIVES_SNIPPETS = [
  [
    'R5Button (contrato alvo)',
    `export function R5Button({ variant = "primary", size = "md", loading, children, ...props }) {
  return (
    <Button
      data-r5-component="button"
      data-r5-variant={variant}
      data-r5-size={size}
      disabled={props.disabled || loading}
      {...props}
    >
      {loading ? "Carregando..." : children}
    </Button>
  );
}`
  ],
  [
    'R5FormField (contrato alvo)',
    `export function R5FormField({ id, label, helperText, error, as = "input", ...props }) {
  const describedBy = error ? \`\${id}-error\` : helperText ? \`\${id}-help\` : undefined;
  return (
    <Box data-r5-component="form-field">
      <label htmlFor={id}>{label}</label>
      <TextField
        id={id}
        select={as === "select"}
        error={Boolean(error)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        {...props}
      />
      {error ? <span id={\`\${id}-error\`}>{error}</span> : helperText ? <span id={\`\${id}-help\`}>{helperText}</span> : null}
    </Box>
  );
}`
  ]
];

const COMPONENT_TECH_DETAILS = {
  Button: {
    sketch: 'button',
    anatomy: 'container, label e ícone opcional',
    states: 'default, hover, active, focus-visible, disabled, loading',
    a11y: 'aria-label quando icon-only e foco 3:1'
  },
  Input: {
    sketch: 'input',
    anatomy: 'label, campo, placeholder e helper/error text',
    states: 'default, focus, filled, error, disabled',
    a11y: 'id/htmlFor, aria-invalid e mensagem associada'
  },
  Select: {
    sketch: 'select',
    anatomy: 'label, valor, ícone de expansão e lista',
    states: 'default, hover, expanded, selected, disabled',
    a11y: 'aria-expanded, teclado (setas/enter/esc)'
  },
  Toast: {
    sketch: 'toast',
    anatomy: 'barra de severidade, título, descrição e ação',
    states: 'success, info, warning, error, dismiss',
    a11y: 'announce não-bloqueante e botão fechar nomeado'
  },
  'Modal/Dialog': {
    sketch: 'dialog',
    anatomy: 'backdrop, header, body e ações',
    states: 'open, closing, confirm, cancel',
    a11y: 'focus trap, esc e aria-modal'
  },
  Drawer: {
    sketch: 'drawer',
    anatomy: 'container lateral, header e conteúdo rolável',
    states: 'closed, opening, open, closing',
    a11y: 'foco inicial no container e esc para fechar'
  },
  Chip: {
    sketch: 'chip',
    anatomy: 'pílula, label e ícone opcional',
    states: 'default, selected, removable, disabled',
    a11y: 'nome acessível e alvo mínimo de clique'
  },
  Accordion: {
    sketch: 'accordion',
    anatomy: 'summary, trigger, content',
    states: 'collapsed, expanded, disabled',
    a11y: 'aria-expanded e heading semântico'
  },
  EnterpriseCard: {
    sketch: 'card',
    anatomy: 'headline, suporte visual e bloco textual',
    states: 'default, hover, featured',
    a11y: 'ordem de leitura e contraste de conteúdo'
  },
  NewsGrid: {
    sketch: 'table',
    anatomy: 'estrutura de linhas/cards, metadata e ação',
    states: 'loading, populated, empty',
    a11y: 'navegação por teclado e landmarks claros'
  }
};

const BLUEPRINT_COMPONENTS = [
  'Button',
  'Input',
  'Select',
  'Toast',
  'Modal/Dialog',
  'Drawer',
  'Chip',
  'Accordion',
  'EnterpriseCard',
  'NewsGrid'
];

const COMPONENT_CODE_SNIPPETS = [
  [
    'Button',
    `<Button variant="contained" color="primary">Ação principal</Button>
<Button variant="outlined" color="primary">Ação secundária</Button>`
  ],
  [
    'Input',
    `<TextField
  label="Título"
  value={title}
  onChange={(e) => setTitle(e.target.value)}
  error={Boolean(error)}
  helperText={error || "Campo obrigatório"}
/>`
  ],
  [
    'Select',
    `<TextField select label="Categoria" value={categoria} onChange={onChange}>
  <MenuItem value="governanca">Governança</MenuItem>
  <MenuItem value="inovacao">Inovação</MenuItem>
</TextField>`
  ],
  [
    'Toast',
    `showToast({
  type: "success",
  title: "Publicado",
  message: "Conteúdo atualizado com sucesso."
});`
  ],
  [
    'Dialog',
    `<Dialog open={open} onClose={onClose}>
  <DialogTitle>Confirmar ação</DialogTitle>
  <DialogContent>Deseja publicar agora?</DialogContent>
</Dialog>`
  ]
];

const COLOR_PRIMARY = ['#FFE5E5', '#FFB8B8', '#FF8A8A', '#F05A5A', '#E63A3A', '#CC0000', '#B00000', '#990000', '#700000', '#4A0000'];
const COLOR_NEUTRAL = ['#FFFFFF', '#F8F9FA', '#E0E0E0', '#BDBDBD', '#666666', '#333333', '#000000'];

const COLOR_MATRIX = [
  ['primary.main', '#CC0000', 'CTA principal'],
  ['secondary.main', '#000000', 'Texto forte e contraste'],
  ['background.default', '#FFFFFF', 'Superfície base'],
  ['background.paper', '#F8F9FA', 'Cartões e blocos'],
  ['text.primary', '#000000', 'Texto principal'],
  ['text.secondary', 'rgba(0,0,0,0.6)', 'Texto secundário']
];

const TYPOGRAPHY_MATRIX = [
  ['fontFamily', '"Inter", "Roboto", "Helvetica", "Arial", sans-serif', 'Base UI'],
  ['h1', '3.5rem / 1.1 / 600 / -0.03em', 'Títulos de impacto'],
  ['h2', '2.5rem / 1.2 / 600 / -0.025em', 'Seções'],
  ['h3', '2rem / 1.2 / 600 / -0.025em', 'Subseções'],
  ['body1', '1rem / 1.6 / 400 / -0.01em', 'Texto principal'],
  ['body2', '0.875rem / 1.6 / 400 / -0.01em', 'Texto auxiliar'],
  ['button', '0.9375rem / 1.2 / 600 / no-caps', 'Ações']
];

const FONT_STACK = [
  ['UI', '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'],
  ['Mono', '"JetBrains Mono", "Fira Code", "SFMono-Regular", Consolas, monospace']
];

const SPACING_MATRIX = [
  ['theme spacing base', '8px', 'MUI default'],
  ['touch target', '44px', 'mobileTokens.touchTarget'],
  ['content max width', '1200px', 'desktopTokens.contentMaxWidth'],
  ['global radius', '14px', 'R5_CHANNEL_TOKENS radius']
];

const MOTION_MATRIX = [
  ['physics.premium', 'cubic-bezier(0.19, 1, 0.22, 1)', 'Transições hero/high-impact'],
  ['physics.smooth', 'cubic-bezier(0.23, 1, 0.32, 1)', 'Interações padrão'],
  ['button transition', 'all 0.4s cubic-bezier(0.23,1,0.32,1)', 'MuiButton root']
];

const USAGE_MATRIX = [
  ['Botões', 'Somente 1 CTA primário por bloco'],
  ['Toasts', 'Consolidar mensagens e evitar empilhamento excessivo'],
  ['Tooltip/Balão', 'Somente apoio contextual, nunca regra crítica'],
  ['Formulários', 'Erro com orientação objetiva de correção'],
  ['UI Shell', 'Header e Footer estáveis; conteúdo rola sem quebrar navegação']
];

const A11Y_MATRIX = [
  ['Texto primário em branco', '#111111 / #FFFFFF', '15.3:1', 'AA/AAA'],
  ['Texto secundário em branco', '#666666 / #FFFFFF', '5.7:1', 'AA'],
  ['Vermelho principal em branco', '#CC0000 / #FFFFFF', '5.9:1', 'AA'],
  ['Texto invertido em escuro', '#FFFFFF / #111111', '15.3:1', 'AA/AAA']
];

const TOKEN_MATRIX = [
  ['--primitive-red-500', '#CC0000', 'Primitivo', 'Cor de marca'],
  ['--color-text-primary', '#111111', 'Semântico', 'Texto principal'],
  ['--color-border-subtle', '#E0E0E0', 'Semântico', 'Bordas suaves'],
  ['--button-height-md', '44px', 'Componente', 'Altura padrão de botão']
];

const API_MATRIX = [
  ['Button', 'variant,size,disabled,loading,icon', 'onClick,onKeyDown', 'aria-label icon-only'],
  ['Input', 'label,value,error,helperText,disabled', 'onChange,onBlur', 'id/htmlFor + aria-invalid'],
  ['Select', 'label,options,value,disabled', 'onChange,onOpen', 'aria-expanded + teclado'],
  ['Dialog', 'open,title,size,closeOnEsc', 'onClose,onPrimaryAction', 'focus trap + esc'],
  ['Toast', 'type,message,action,duration', 'onClose,onAction', 'announce non-blocking feedback'],
  ['CookieConsent', 'open,preferences,requiredCategories', 'onAcceptAll,onSave', 'controles acessíveis de consentimento'],
  ['FloatingMenu', 'visible,items,activeSection', 'onNavigate,onToggle', 'foco visível e navegação por teclado'],
  ['NewsShareWidget', 'url,title,channels', 'onShare,onCopy', 'botões com nome acessível']
];

const TESTING_MATRIX = [
  ['npm run build', 'Build de produção obrigatório'],
  ['npm test', 'Cobertura funcional base'],
  ['check:channel-isolation', 'Garante isolamento desktop/mobile']
];

const QUALITY_GATES = [
  ['Lint de tokens', 'Padrão de nomenclatura consistente'],
  ['Regressão visual', 'Detecta regressão de layout/estilo'],
  ['Checks de A11y', 'Contraste/foco/roles mínimos'],
  ['Gate de build', 'Bloqueia merge em falha']
];

const CARBON_BORDER = alpha('#161616', 0.2);
const CARBON_SURFACE = '#F4F4F4';
const CARBON_SURFACE_ALT = '#FFFFFF';

const HEAD_CELL_SX = {
  fontWeight: 800,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  bgcolor: '#F4F4F4',
  borderColor: alpha('#161616', 0.12),
  py: 0.55
};

const BODY_CELL_SX = {
  fontSize: '0.8rem',
  borderColor: alpha('#161616', 0.1),
  color: '#393939',
  py: 0.55
};

const DenseTable = ({ headers, rows, monoFirst = false }) => (
  <Table
    size="small"
    sx={{
      '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': { bgcolor: '#FCFCFC' },
      '& .MuiTableBody-root .MuiTableRow-root:hover': { bgcolor: alpha('#0F62FE', 0.04) }
    }}
  >
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell key={header} sx={HEAD_CELL_SX}>{header}</TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row[0]}>
          {row.map((col, index) => (
            <TableCell
              key={`${row[0]}-${index}`}
              sx={index === 0 && monoFirst ? { ...BODY_CELL_SX, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem' } : BODY_CELL_SX}
            >
              {col}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const TechnicalLabel = ({ label, value }) => (
  <Box sx={{ mb: 0.48 }}>
    <Typography sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6F6F6F', fontWeight: 800 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.73rem', color: '#393939', lineHeight: 1.35 }}>
      {value}
    </Typography>
  </Box>
);

const ComponentSketch = ({ type = 'card' }) => {
  if (type === 'button') {
    return (
      <Stack direction="row" spacing={0.6} justifyContent="center">
        <Box sx={{ height: 26, width: 70, borderRadius: 999, bgcolor: '#CC0000' }} />
        <Box sx={{ height: 26, width: 56, borderRadius: 999, border: '1px solid', borderColor: '#8D8D8D', bgcolor: '#FFFFFF' }} />
      </Stack>
    );
  }
  if (type === 'input' || type === 'select') {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ width: 64, height: 6, bgcolor: alpha('#161616', 0.2), borderRadius: 10, mb: 0.6 }} />
        <Box sx={{ height: 30, borderRadius: 1.6, border: '1px solid', borderColor: '#BDBDBD', position: 'relative', bgcolor: '#FFFFFF' }}>
          {type === 'select' && <Box sx={{ position: 'absolute', right: 8, top: 9, width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '6px solid #666' }} />}
        </Box>
      </Box>
    );
  }
  if (type === 'toast') {
    return (
      <Box sx={{ border: '1px solid', borderColor: '#BDBDBD', borderRadius: 1.2, bgcolor: '#FFFFFF', overflow: 'hidden' }}>
        <Box sx={{ width: 4, bgcolor: '#CC0000', height: 42, float: 'left' }} />
        <Box sx={{ ml: 1.5, py: 0.7 }}>
          <Box sx={{ width: '58%', height: 6, bgcolor: alpha('#161616', 0.25), borderRadius: 10, mb: 0.5 }} />
          <Box sx={{ width: '72%', height: 5, bgcolor: alpha('#161616', 0.14), borderRadius: 10 }} />
        </Box>
      </Box>
    );
  }
  if (type === 'dialog') {
    return (
      <Box sx={{ bgcolor: alpha('#161616', 0.08), borderRadius: 1.6, p: 1 }}>
        <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid', borderColor: '#BDBDBD', borderRadius: 1.2, p: 0.8 }}>
          <Box sx={{ width: '60%', height: 6, bgcolor: alpha('#161616', 0.22), borderRadius: 10, mb: 0.6 }} />
          <Box sx={{ width: '86%', height: 5, bgcolor: alpha('#161616', 0.12), borderRadius: 10, mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <Box sx={{ width: 38, height: 16, borderRadius: 99, border: '1px solid', borderColor: '#999' }} />
            <Box sx={{ width: 44, height: 16, borderRadius: 99, bgcolor: '#CC0000' }} />
          </Box>
        </Box>
      </Box>
    );
  }
  if (type === 'drawer') {
    return (
      <Box sx={{ border: '1px solid', borderColor: '#BDBDBD', borderRadius: 1.2, bgcolor: alpha('#161616', 0.05), p: 0.8 }}>
        <Box sx={{ width: '42%', height: 46, bgcolor: '#FFFFFF', border: '1px solid', borderColor: '#C7C7C7', borderRadius: 1.2 }} />
      </Box>
    );
  }
  if (type === 'chip') {
    return (
      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
        <Box sx={{ width: 58, height: 22, borderRadius: 99, border: '1px solid', borderColor: '#8D8D8D', bgcolor: '#FFFFFF' }} />
        <Box sx={{ width: 64, height: 22, borderRadius: 99, bgcolor: alpha('#0F62FE', 0.12), border: '1px solid', borderColor: alpha('#0F62FE', 0.4) }} />
      </Box>
    );
  }
  if (type === 'accordion') {
    return (
      <Box sx={{ border: '1px solid', borderColor: '#BDBDBD', borderRadius: 1.2, bgcolor: '#FFFFFF' }}>
        <Box sx={{ height: 28, px: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderBottomColor: '#E0E0E0' }}>
          <Box sx={{ width: '62%', height: 6, bgcolor: alpha('#161616', 0.2), borderRadius: 10 }} />
          <ChevronRightIcon sx={{ fontSize: 13, color: '#777' }} />
        </Box>
        <Box sx={{ p: 0.8 }}>
          <Box sx={{ width: '82%', height: 5, bgcolor: alpha('#161616', 0.12), borderRadius: 10 }} />
        </Box>
      </Box>
    );
  }
  if (type === 'table') {
    return (
      <Box sx={{ border: '1px solid', borderColor: '#BDBDBD', borderRadius: 1.2, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
        <Box sx={{ height: 18, bgcolor: '#F4F4F4', borderBottom: '1px solid', borderBottomColor: '#E0E0E0' }} />
        {[0, 1, 2].map((row) => <Box key={row} sx={{ height: 14, borderBottom: row < 2 ? '1px solid' : 'none', borderBottomColor: '#EFEFEF' }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: '#BDBDBD', borderRadius: 1.2, bgcolor: '#FFFFFF', p: 0.8 }}>
      <Box sx={{ width: '62%', height: 6, bgcolor: alpha('#161616', 0.2), borderRadius: 10, mb: 0.5 }} />
      <Box sx={{ width: '82%', height: 5, bgcolor: alpha('#161616', 0.12), borderRadius: 10 }} />
    </Box>
  );
};

const CodeBlock = ({ value }) => (
  <Box
    sx={{
      bgcolor: '#161616',
      color: '#F4F4F4',
      borderRadius: 0,
      p: 0.9,
      border: '1px solid',
      borderColor: alpha('#FFFFFF', 0.08),
      overflowX: 'auto'
    }}
  >
    <Typography
      component="pre"
      sx={{
        m: 0,
        whiteSpace: 'pre',
        fontSize: '0.72rem',
        lineHeight: 1.45,
        fontFamily: '"JetBrains Mono", monospace'
      }}
    >
      {value}
    </Typography>
  </Box>
);

const CarbonSection = ({ id, title, children }) => (
  <Paper
    id={id}
    variant="outlined"
    sx={{
      borderRadius: 0.2,
      borderColor: alpha('#161616', 0.14),
      p: 1,
      scrollMarginTop: '220px'
    }}
    data-ds-section="true"
  >
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, mb: 0.6 }}>{title}</Typography>
    {children}
  </Paper>
);

const DesignSystemPage = () => {
  const [tab, setTab] = useState(0);
  const [navSearch, setNavSearch] = useState('');
  const [activeItem, setActiveItem] = useState('sec-overview');
  const [librarySearch, setLibrarySearch] = useState('');
  const [category, setCategory] = useState('Todos');
  const [gridMode, setGridMode] = useState(true);
  const mainScrollRef = useRef(null);

  const navById = useMemo(
    () => Object.fromEntries(NAV_ITEMS.map((item) => [item.id, item])),
    []
  );

  const groupedNav = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        group,
        items: NAV_ITEMS.filter(
          (item) =>
            item.group === group &&
            item.label.toLowerCase().includes(navSearch.toLowerCase())
        )
      })).filter((group) => group.items.length > 0),
    [navSearch]
  );

  const categories = useMemo(
    () => ['Todos', ...new Set(COMPONENT_LIBRARY.map((item) => item[1]))],
    []
  );

  const filteredLibrary = useMemo(
    () =>
      COMPONENT_LIBRARY.filter((item) => {
        const byCategory = category === 'Todos' || item[1] === category;
        const bySearch = `${item[0]} ${item[1]} ${item[3]} ${item[4]}`
          .toLowerCase()
          .includes(librarySearch.toLowerCase());
        return byCategory && bySearch;
      }),
    [category, librarySearch]
  );

  const filteredLibraryWithDetails = useMemo(
    () =>
      filteredLibrary.map((item) => {
        const tech = COMPONENT_TECH_DETAILS[item[0]] || {
          sketch: 'card',
          anatomy: 'estrutura base e composição visual',
          states: 'default e variações do fluxo',
          a11y: 'foco visível e nome acessível'
        };
        return {
          name: item[0],
          category: item[1],
          status: item[2],
          source: item[3],
          notes: item[4],
          ...tech
        };
      }),
    [filteredLibrary]
  );

  useEffect(() => {
    const rootNode = mainScrollRef.current;
    const nodes = Array.from(document.querySelectorAll('[data-ds-section="true"]'));
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible?.target?.id) return;
        const id = visible.target.id;
        setActiveItem(id);
        const targetTab = navById[id]?.tab;
        if (typeof targetTab === 'number') {
          setTab((prev) => (prev === targetTab ? prev : targetTab));
        }
      },
      { root: rootNode, rootMargin: '-35% 0px -55% 0px', threshold: [0.1, 0.25, 0.5] }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [navById]);

  const firstByTab = useMemo(
    () => ({ 0: 'sec-overview', 1: 'sec-colors', 2: 'sec-tokens' }),
    []
  );

  const goToSection = (item) => {
    setActiveItem(item.id);
    setTab(item.tab);
    window.setTimeout(() => {
      const node = document.getElementById(item.id);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleTabChange = (_, nextTab) => {
    setTab(nextTab);
    const targetId = firstByTab[nextTab];
    if (!targetId) return;
    setActiveItem(targetId);
    window.setTimeout(() => {
      const node = document.getElementById(targetId);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const showLibraryControls = tab === 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F4F4' }}>
      <Box
        sx={{
          height: MASTHEAD_H,
          bgcolor: '#161616',
          color: '#F4F4F4',
          borderBottom: '1px solid',
          borderColor: '#393939',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          gap: 1
        }}
      >
        <IconButton size="small" sx={{ color: '#F4F4F4' }}>
          <MenuIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>R5ID Sistema de Design</Typography>
        <Chip label={DOC_VERSION} size="small" sx={{ ml: 0.8, bgcolor: '#262626', color: '#F4F4F4' }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '300px 1fr' }, height: `calc(100vh - ${MASTHEAD_H}px)`, overflow: 'hidden' }}>
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRight: { lg: '1px solid' },
            borderColor: { lg: alpha('#161616', 0.14) },
            height: `calc(100vh - ${MASTHEAD_H}px)`,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            scrollbarGutter: 'stable'
          }}
        >
          <Box sx={{ px: 1.4, pt: 1.2, pb: 0.8 }}>
            <Typography sx={{ fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6F6F6F', fontWeight: 700, mb: 0.35 }}>
              Navegação
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar seção"
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ p: 1.1 }}>
            <Stack spacing={1.1}>
              {groupedNav.map((group) => (
                <Box key={group.group}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#6F6F6F', fontWeight: 800, letterSpacing: '0.08em', mb: 0.35 }}>
                    {group.group.toUpperCase()}
                  </Typography>
                  <List dense disablePadding>
                    {group.items.map((item) => (
                      <ListItemButton
                        key={item.id}
                        onClick={() => goToSection(item)}
                        sx={{
                          py: 0.6,
                          px: 0.9,
                          borderRadius: 0.5,
                          mb: 0.2,
                          bgcolor: activeItem === item.id ? alpha('#0F62FE', 0.08) : 'transparent',
                          borderLeft: '2px solid',
                          borderLeftColor: activeItem === item.id ? '#0F62FE' : 'transparent',
                          transition: 'background-color 160ms ease'
                        }}
                      >
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: '0.81rem', color: activeItem === item.id ? '#161616' : '#525252', fontWeight: activeItem === item.id ? 700 : 600 }}
                        />
                        <ChevronRightIcon sx={{ fontSize: 15, color: '#8D8D8D' }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box
          ref={mainScrollRef}
          sx={{ minWidth: 0, bgcolor: '#FFFFFF', height: `calc(100vh - ${MASTHEAD_H}px)`, overflowY: 'auto', overscrollBehavior: 'contain', scrollbarGutter: 'stable' }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bgcolor: alpha('#FFFFFF', 0.96),
              backdropFilter: 'blur(6px)',
              borderBottom: '1px solid',
              borderColor: alpha('#161616', 0.12)
            }}
          >
            <Box sx={{ px: { xs: 2, lg: 3 }, py: 1.4 }}>
              <Typography sx={{ fontSize: '0.76rem', color: '#6F6F6F', mb: 0.2 }}>Design System / Desktop</Typography>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, letterSpacing: '-0.01em', mb: 0.6 }}>Portal Desktop R5ID</Typography>
              <Typography sx={{ color: '#525252', maxWidth: 920 }}>
                Navegação em padrão Carbon com dados reais de tema, tokens e componentes do portal desktop.
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 2, lg: 3 }, py: 0.65, borderTop: '1px solid', borderColor: alpha('#161616', 0.1), bgcolor: '#FAFAFA' }}>
              <Tabs value={tab} onChange={handleTabChange} sx={{ minHeight: 34, '& .MuiTab-root': { minHeight: 34, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700 } }}>
                <Tab label="Biblioteca" />
                <Tab label="Uso" />
                <Tab label="Código" />
              </Tabs>
            </Box>

            <Box sx={{ px: { xs: 2, lg: 3 }, py: 0.9, borderTop: '1px solid', borderColor: alpha('#161616', 0.1), bgcolor: '#FFFFFF' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={showLibraryControls ? 'Buscar componentes' : 'Buscar seções'}
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    )
                  }}
                />
                {showLibraryControls && (
                  <>
                    <Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      size="small"
                      startAdornment={<FilterListIcon sx={{ fontSize: 18, mr: 0.6, color: '#6F6F6F' }} />}
                      sx={{ minWidth: 180 }}
                    >
                      {categories.map((item) => (
                        <MenuItem key={item} value={item}>{item}</MenuItem>
                      ))}
                    </Select>
                    <Stack direction="row" spacing={0.4}>
                      <IconButton size="small" onClick={() => setGridMode(true)} sx={{ border: '1px solid', borderColor: alpha('#161616', 0.14), borderRadius: 0.6, bgcolor: gridMode ? alpha('#0F62FE', 0.12) : '#FFFFFF' }}>
                        <GridViewIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setGridMode(false)} sx={{ border: '1px solid', borderColor: alpha('#161616', 0.14), borderRadius: 0.6, bgcolor: !gridMode ? alpha('#0F62FE', 0.12) : '#FFFFFF' }}>
                        <ViewListIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" sx={{ border: '1px solid', borderColor: alpha('#161616', 0.14), borderRadius: 0.6 }}>
                        <TuneIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Stack>
                  </>
                )}
              </Stack>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 2, lg: 3 }, py: 1.3, pb: 4 }}>
            {tab === 0 && (
              <Stack spacing={1}>
                <CarbonSection id="sec-overview" title="Visão geral">
                  <Typography sx={{ fontSize: '0.82rem', color: '#393939' }}>
                    Base de UI do portal desktop com tema MUI centralizado, paleta institucional (#CC0000/#000000),
                    tipografia Inter e raio global de 14px.
                  </Typography>
                </CarbonSection>

                <CarbonSection id="sec-foundations" title="Fundamentos">
                  <DenseTable headers={['Item', 'Valor', 'Origem']} rows={SPACING_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-library" title="Biblioteca">
                  {gridMode ? (
                    <Grid container spacing={1}>
                      {filteredLibraryWithDetails.map((item) => (
                        <Grid key={item.name} item xs={12} sm={6} md={4}>
                          <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 1.1, height: '100%', bgcolor: CARBON_SURFACE }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                              <Typography sx={{ fontSize: '0.86rem', fontWeight: 800 }}>{item.name}</Typography>
                              <Chip
                                label={item.status}
                                size="small"
                                sx={{
                                  borderRadius: 0.4,
                                  height: 20,
                                  bgcolor: item.status === 'Pronto' ? alpha('#24A148', 0.12) : alpha('#0F62FE', 0.12),
                                  color: item.status === 'Pronto' ? '#1E7A34' : '#0F62FE',
                                  fontSize: '0.68rem',
                                  fontWeight: 700
                                }}
                              />
                            </Stack>
                            <Typography sx={{ fontSize: '0.72rem', color: '#6F6F6F', mb: 0.5 }}>{item.category}</Typography>
                            <Box sx={{ mb: 0.7 }}>
                              <ComponentSketch type={item.sketch} />
                            </Box>
                            <TechnicalLabel label="Descrição técnica" value={item.notes} />
                            <TechnicalLabel label="Anatomia" value={item.anatomy} />
                            <TechnicalLabel label="Estados" value={item.states} />
                            <TechnicalLabel label="A11y" value={item.a11y} />
                            <Typography sx={{ fontSize: '0.68rem', color: '#6F6F6F', mt: 0.35, fontFamily: '"JetBrains Mono", monospace' }}>
                              {item.source}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <DenseTable
                      headers={['Componente', 'Categoria', 'Status', 'Descrição técnica', 'Anatomia', 'Estados', 'A11y', 'Origem']}
                      rows={filteredLibraryWithDetails.map((item) => ([
                        item.name,
                        item.category,
                        item.status,
                        item.notes,
                        item.anatomy,
                        item.states,
                        item.a11y,
                        item.source
                      ]))}
                    />
                  )}
                  {!filteredLibraryWithDetails.length && (
                    <Typography sx={{ fontSize: '0.82rem', color: '#6F6F6F' }}>Nenhum componente encontrado com os filtros atuais.</Typography>
                  )}
                </CarbonSection>

                <CarbonSection id="sec-reverse-engineering" title="Engenharia reversa dos elementos do portal">
                  <Typography sx={{ fontSize: '0.8rem', color: '#525252', mb: 0.8 }}>
                    Inventário técnico dos elementos críticos (desktop x mobile) para migração gradual sem regressão cruzada.
                  </Typography>
                  <DenseTable
                    headers={['Elemento', 'Desktop (estado atual)', 'Mobile (estado atual)', 'Padrão alvo']}
                    rows={REVERSE_ENGINEERING_MATRIX}
                  />
                  <Divider sx={{ my: 0.8 }} />
                  <Grid container spacing={1}>
                    {CORE_UI_BLUEPRINTS.map((item) => (
                      <Grid item xs={12} md={6} lg={4} key={item.name}>
                        <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 1, height: '100%', bgcolor: CARBON_SURFACE }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, mb: 0.6 }}>{item.name}</Typography>
                          <Box sx={{ mb: 0.7 }}>
                            <ComponentSketch type={item.sketch} />
                          </Box>
                          <TechnicalLabel label="Anatomia" value={item.anatomy} />
                          <TechnicalLabel label="Estados" value={item.states} />
                          <TechnicalLabel label="A11y" value={item.a11y} />
                          <TechnicalLabel label="Desktop (referência)" value={item.desktopRef} />
                          <TechnicalLabel label="Mobile (referência)" value={item.mobileRef} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CarbonSection>

                <CarbonSection id="sec-target-core" title="Padrão alvo: Button + Form Field">
                  <Typography sx={{ fontSize: '0.8rem', color: '#525252', mb: 0.8 }}>
                    Contrato técnico inicial para unificar os elementos mais recorrentes em Desktop e Mobile com menor impacto.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 1, height: '100%', bgcolor: CARBON_SURFACE_ALT }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, mb: 0.6 }}>Button</Typography>
                        <Box sx={{ mb: 0.7 }}>
                          <ComponentSketch type="button" />
                        </Box>
                        <DenseTable headers={['Atributo', 'Padrão alvo']} rows={BUTTON_TARGET_MATRIX} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 1, height: '100%', bgcolor: CARBON_SURFACE_ALT }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, mb: 0.6 }}>Form Field</Typography>
                        <Box sx={{ mb: 0.7 }}>
                          <ComponentSketch type="input" />
                        </Box>
                        <DenseTable headers={['Atributo', 'Padrão alvo']} rows={FORM_FIELD_TARGET_MATRIX} />
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 0.8 }} />
                  <DenseTable headers={['Etapa', 'Entrega', 'Critério de aceite']} rows={CORE_MIGRATION_STEPS} />
                </CarbonSection>

                <CarbonSection id="sec-component-blueprints" title="Especificação técnica + desenho">
                  <Grid container spacing={1}>
                    {BLUEPRINT_COMPONENTS.map((name) => {
                      const details = COMPONENT_TECH_DETAILS[name];
                      if (!details) return null;
                      return (
                        <Grid item xs={12} md={6} lg={4} key={name}>
                          <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 1, bgcolor: CARBON_SURFACE_ALT }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, mb: 0.6 }}>{name}</Typography>
                            <Box sx={{ mb: 0.7 }}>
                              <ComponentSketch type={details.sketch} />
                            </Box>
                            <TechnicalLabel label="Anatomia" value={details.anatomy} />
                            <TechnicalLabel label="Estados" value={details.states} />
                            <TechnicalLabel label="A11y" value={details.a11y} />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CarbonSection>

                <CarbonSection id="sec-carbon-placement" title="Mapa Carbon → Portal R5ID">
                  <DenseTable
                    headers={['Família', 'Componentes mapeados no portal', 'Objetivo']}
                    rows={CARBON_PLACEMENT_MATRIX}
                  />
                </CarbonSection>

                <CarbonSection id="sec-governance" title="Governança">
                  <DenseTable
                    headers={['Regra', 'Valor']}
                    rows={[
                      ['Branch de produção', 'master'],
                      ['Path desktop', 'impl_frontend_desktop/**'],
                      ['Workflow desktop', 'Deploy Frontend (Prod) to S3 + Invalidate CloudFront'],
                      ['Isolamento', 'Desktop e Mobile separados por pasta e pipeline']
                    ]}
                  />
                </CarbonSection>
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={1}>
                <CarbonSection id="sec-colors" title="Cores">
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.4 }}>Escala primária</Typography>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {COLOR_PRIMARY.map((color) => (
                          <Box key={color} sx={{ width: 50 }}>
                            <Box sx={{ height: 20, bgcolor: color, border: '1px solid', borderColor: alpha('#161616', 0.16), borderRadius: 0.3 }} />
                            <Typography sx={{ fontSize: '0.62rem', color: '#6F6F6F', mt: 0.2 }}>{color}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.4 }}>Escala neutra</Typography>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {COLOR_NEUTRAL.map((color) => (
                          <Box key={color} sx={{ width: 50 }}>
                            <Box sx={{ height: 20, bgcolor: color, border: '1px solid', borderColor: alpha('#161616', 0.16), borderRadius: 0.3 }} />
                            <Typography sx={{ fontSize: '0.62rem', color: '#6F6F6F', mt: 0.2 }}>{color}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 0.7 }} />
                  <DenseTable headers={['Token', 'Valor', 'Uso']} rows={COLOR_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-typography" title="Tipografia">
                  <DenseTable headers={['Nível', 'Especificação', 'Uso']} rows={TYPOGRAPHY_MATRIX} />
                  <Divider sx={{ my: 0.7 }} />
                  <DenseTable headers={['Família', 'Stack']} rows={FONT_STACK} />
                </CarbonSection>

                <CarbonSection id="sec-spacing" title="Espaçamento">
                  <DenseTable headers={['Item', 'Valor', 'Origem']} rows={SPACING_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-motion" title="Movimento">
                  <DenseTable headers={['Token', 'Valor', 'Uso']} rows={MOTION_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-usage" title="Uso">
                  <DenseTable headers={['Elemento', 'Diretriz']} rows={USAGE_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-a11y" title="Acessibilidade">
                  <DenseTable headers={['Uso', 'Par', 'Razão', 'Nível']} rows={A11Y_MATRIX} />
                </CarbonSection>
              </Stack>
            )}

            {tab === 2 && (
              <Stack spacing={1}>
                <CarbonSection id="sec-tokens" title="Matriz de tokens">
                  <DenseTable headers={['Token', 'Valor', 'Tipo', 'Uso']} rows={TOKEN_MATRIX} monoFirst />
                </CarbonSection>

                <CarbonSection id="sec-code-snippets" title="Snippets de implementação">
                  <Grid container spacing={1}>
                    {COMPONENT_CODE_SNIPPETS.map(([title, snippet]) => (
                      <Grid item xs={12} md={6} key={title}>
                        <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 0.9, height: '100%', bgcolor: CARBON_SURFACE_ALT }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.6 }}>{title}</Typography>
                          <CodeBlock value={snippet} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ my: 0.8 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.6 }}>Contratos alvo (próxima onda)</Typography>
                  <Grid container spacing={1}>
                    {CORE_PRIMITIVES_SNIPPETS.map(([title, snippet]) => (
                      <Grid item xs={12} md={6} key={title}>
                        <Box sx={{ border: '1px solid', borderColor: CARBON_BORDER, borderRadius: 0, p: 0.9, height: '100%', bgcolor: CARBON_SURFACE_ALT }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.6 }}>{title}</Typography>
                          <CodeBlock value={snippet} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CarbonSection>

                <CarbonSection id="sec-api" title="Matriz de API de componentes">
                  <DenseTable headers={['Componente', 'Props críticas', 'Eventos', 'Contrato A11y']} rows={API_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-testing" title="Matriz de testes">
                  <DenseTable headers={['Comando', 'Objetivo']} rows={TESTING_MATRIX} />
                </CarbonSection>

                <CarbonSection id="sec-quality" title="Matriz de quality gates">
                  <DenseTable headers={['Gate', 'Objetivo']} rows={QUALITY_GATES} />
                </CarbonSection>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DesignSystemPage;
